import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users, DollarSign, TrendingUp, FileText, Search, ChevronDown,
  ArrowLeft, RefreshCw, Trash2, Shield, BarChart3, CreditCard,
  UserCheck, Activity, Filter, Download, Globe, PenSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colors = {
    blue: 'from-blue-600/20 to-blue-700/10 border-blue-500/30',
    green: 'from-emerald-600/20 to-emerald-700/10 border-emerald-500/30',
    amber: 'from-amber-600/20 to-amber-700/10 border-amber-500/30',
    violet: 'from-violet-600/20 to-violet-700/10 border-violet-500/30',
  };
  const iconColors = { blue: 'text-blue-400', green: 'text-emerald-400', amber: 'text-amber-400', violet: 'text-violet-400' };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${iconColors[color]}`} />
        {sub && <span className="text-xs text-slate-300">{sub}</span>}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-300 mt-1">{label}</p>
    </div>
  );
}

function UserRow({ user, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(user.role);
  const [tier, setTier] = useState(user.subscription_tier || 'free');

  const save = async () => {
    await onUpdate(user.id, { role, subscription_tier: tier });
    setEditing(false);
  };

  return (
    <tr className="border-b border-slate-700/50 hover:bg-slate-800/30">
      <td className="py-3 px-4">
        <div>
          <p className="text-white text-sm font-medium">{user.full_name || '-'}</p>
          <p className="text-slate-300 text-xs">{user.email}</p>
        </div>
      </td>
      <td className="py-3 px-4">
        {editing ? (
          <select value={role} onChange={e => setRole(e.target.value)} className="bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-600">
            {['student', 'parent', 'admin', 'org_admin', 'super_admin', 'counselor', 'employer_admin'].map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        ) : (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-300">{user.role}</span>
        )}
      </td>
      <td className="py-3 px-4">
        {editing ? (
          <select value={tier} onChange={e => setTier(e.target.value)} className="bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-600">
            {['free', 'student', 'family', 'enterprise'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-slate-300">{user.subscription_tier || 'free'}</span>
        )}
      </td>
      <td className="py-3 px-4 text-xs text-slate-300">{user.created_at?.slice(0, 10)}</td>
      <td className="py-3 px-4">
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={save}>Save</Button>
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setEditing(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setEditing(true)}>Edit</Button>
              <Button size="sm" variant="ghost" className="text-xs h-7 text-red-400 hover:text-red-300" onClick={() => onDelete(user.id)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminDashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [payments, setPayments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [usersPage, setUsersPage] = useState(0);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/dashboard`, { headers });
      if (!res.ok) throw new Error('Access denied');
      setDashboard(await res.json());
    } catch (e) {
      toast.error(e.message);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    const params = new URLSearchParams({ limit: '50', skip: String(usersPage * 50) });
    if (roleFilter) params.set('role', roleFilter);
    if (searchQuery) params.set('search', searchQuery);
    try {
      const res = await fetch(`${API}/admin/users?${params}`, { headers });
      const data = await res.json();
      setUsers(data.users || []);
      setUsersTotal(data.total || 0);
    } catch {}
  }, [token, roleFilter, searchQuery, usersPage]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/payments?limit=20`, { headers });
      const data = await res.json();
      setPayments(data.payments || []);
    } catch {}
  }, [token]);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/leads?limit=30`, { headers });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!user || !['super_admin', 'admin', 'org_admin'].includes(user.role)) {
      navigate('/login');
      return;
    }
    setLoading(true);
    Promise.all([fetchDashboard(), fetchUsers(), fetchPayments(), fetchLeads()])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(); }, [roleFilter, searchQuery, usersPage]);

  const handleUpdateUser = async (userId, updates) => {
    try {
      await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      toast.success('User updated');
      fetchUsers();
    } catch { toast.error('Update failed'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await fetch(`${API}/admin/users/${userId}`, { method: 'DELETE', headers });
      toast.success('User deleted');
      fetchUsers();
      fetchDashboard();
    } catch { toast.error('Delete failed'); }
  };

  const handleExportLeads = async () => {
    try {
      const res = await fetch(`${API}/admin/leads/export`, { headers });
      const data = await res.json();
      const csv = ['Name,Email,Status,Pathway,Created'].concat(
        (data.leads || []).map(l => `"${l.full_name}","${l.email}","${l.status}","${l.pathway_preference}","${l.created_at?.slice(0, 10)}"`)
      ).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'leads_export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const ov = dashboard?.overview || {};
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'leads', label: 'CRM Leads', icon: UserCheck },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1b30]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-slate-300 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-semibold">Admin Dashboard</h1>
                <p className="text-xs text-slate-300">{user?.email}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'super_admin' && (
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/tenants')} data-testid="tenant-management-btn">
                <Globe className="w-4 h-4 mr-2" /> Tenants
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate('/app/admin/content')} data-testid="content-studio-btn">
              <PenSquare className="w-4 h-4 mr-2" /> Content Studio
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/consent-audit')} data-testid="consent-audit-btn">
              <FileText className="w-4 h-4 mr-2" /> Consent Audit
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/exam-dates')} data-testid="exam-dates-btn">
              <FileText className="w-4 h-4 mr-2" /> Exam Dates
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/deployment-status')} data-testid="deployment-status-btn">
              <FileText className="w-4 h-4 mr-2" /> Deployment Status
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/qa-console')} data-testid="qa-console-btn">
              <FileText className="w-4 h-4 mr-2" /> QA Console
            </Button>
            <Button size="sm" variant="outline" onClick={() => { fetchDashboard(); fetchUsers(); }} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-800 bg-[#0d1b30]/50">
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-300 hover:text-slate-300'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={ov.total_users || 0} sub="all time" color="blue" />
              <StatCard icon={TrendingUp} label="New Signups" value={ov.recent_signups_7d || 0} sub="last 7 days" color="green" />
              <StatCard icon={Activity} label="Active Users" value={ov.active_users_30d || 0} sub="last 30 days" color="amber" />
              <StatCard icon={DollarSign} label="Revenue" value={`$${(ov.total_revenue || 0).toFixed(2)}`} sub="total" color="violet" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Users by Role */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-300 mb-4">Users by Role</h3>
                <div className="space-y-3">
                  {Object.entries(dashboard?.users_by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300 capitalize">{role.replace('_', ' ')}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(count / (ov.total_users || 1)) * 100}%` }} />
                        </div>
                        <span className="text-sm text-white font-medium w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-slate-300 mb-4">Platform Content</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{dashboard?.content?.total_goals || 0}</p>
                    <p className="text-xs text-slate-300 mt-1">Goals Created</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{dashboard?.content?.total_paths || 0}</p>
                    <p className="text-xs text-slate-300 mt-1">Paths Selected</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{dashboard?.content?.total_documents || 0}</p>
                    <p className="text-xs text-slate-300 mt-1">Documents</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-white">{dashboard?.crm?.new_leads || 0}</p>
                    <p className="text-xs text-slate-300 mt-1">New Leads</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Distribution */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-slate-300 mb-4">Subscription Tiers</h3>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(dashboard?.users_by_subscription || {}).map(([tier, count]) => (
                  <div key={tier} className="bg-slate-800/50 rounded-lg px-4 py-3 flex items-center gap-3">
                    <span className="text-sm text-slate-300 capitalize">{tier}</span>
                    <span className="text-lg font-bold text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="space-y-4">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setUsersPage(0); }}
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                  data-testid="user-search-input"
                />
              </div>
              <select
                value={roleFilter}
                onChange={e => { setRoleFilter(e.target.value); setUsersPage(0); }}
                className="bg-slate-900 text-white text-sm rounded-md px-3 py-2 border border-slate-700"
                data-testid="role-filter"
              >
                <option value="">All Roles</option>
                {['student', 'parent', 'admin', 'org_admin', 'super_admin', 'counselor', 'employer_admin'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <span className="text-sm text-slate-300">{usersTotal} users</span>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-300 uppercase">
                    <th className="py-3 px-4 text-left">User</th>
                    <th className="py-3 px-4 text-left">Role</th>
                    <th className="py-3 px-4 text-left">Tier</th>
                    <th className="py-3 px-4 text-left">Joined</th>
                    <th className="py-3 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <UserRow key={u.id} user={u} onUpdate={handleUpdateUser} onDelete={handleDeleteUser} />
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center text-slate-300 py-8">No users found</p>
              )}
            </div>

            {usersTotal > 50 && (
              <div className="flex justify-center gap-2">
                <Button size="sm" variant="outline" disabled={usersPage === 0} onClick={() => setUsersPage(p => p - 1)}>Previous</Button>
                <span className="text-sm text-slate-300 py-2">Page {usersPage + 1} of {Math.ceil(usersTotal / 50)}</span>
                <Button size="sm" variant="outline" disabled={(usersPage + 1) * 50 >= usersTotal} onClick={() => setUsersPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {tab === 'payments' && (
          <div className="space-y-4">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-300 uppercase">
                    <th className="py-3 px-4 text-left">Date</th>
                    <th className="py-3 px-4 text-left">User</th>
                    <th className="py-3 px-4 text-left">Type</th>
                    <th className="py-3 px-4 text-left">Amount</th>
                    <th className="py-3 px-4 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-slate-700/50">
                      <td className="py-3 px-4 text-sm text-slate-300">{p.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-4 text-sm text-white">{p.user_id?.slice(0, 8)}...</td>
                      <td className="py-3 px-4 text-sm text-slate-300 capitalize">{p.type}</td>
                      <td className="py-3 px-4 text-sm text-white font-medium">${p.amount?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          p.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                        }`}>{p.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-slate-300 py-8">No payments yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CRM Leads Tab */}
        {tab === 'leads' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-300">{leads.length} leads</span>
              <Button size="sm" variant="outline" onClick={handleExportLeads} data-testid="export-leads-btn">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-300 uppercase">
                    <th className="py-3 px-4 text-left">Name</th>
                    <th className="py-3 px-4 text-left">Email</th>
                    <th className="py-3 px-4 text-left">Pathway</th>
                    <th className="py-3 px-4 text-left">Status</th>
                    <th className="py-3 px-4 text-left">Signed Up</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={i} className="border-b border-slate-700/50">
                      <td className="py-3 px-4 text-sm text-white">{l.full_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-300">{l.email}</td>
                      <td className="py-3 px-4 text-sm text-slate-300 capitalize">{l.pathway_preference || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          l.status === 'new' ? 'bg-blue-500/20 text-blue-300' :
                          l.status === 'converted' ? 'bg-emerald-500/20 text-emerald-300' :
                          'bg-slate-500/20 text-slate-300'
                        }`}>{l.status}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300">{l.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-slate-300 py-8">No leads yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
